import { NgStyle } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ComponentRef,
    effect,
    HostListener,
    inject,
    Injector,
    OnDestroy,
    Signal,
    StaticProvider,
    Type,
    ViewChild,
    viewChildren,
    ViewContainerRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconKey } from '@common/classes/icons';
import { ButtonComponent } from '@common/components/button/button.component';
import { BaseInputComponent } from '@common/components/input/base-input/base-input.component';
import { InputComponent } from '@common/components/input/input.component';
import { InputConfig, InputType } from '@common/components/input/input.type';
import { CrudAction } from '@common/known-types/crud-action.codes';
import { ListFilterModel } from '@common/models/list-filter.model';
import { CommonService } from '@common/services/common.service';
import { environment } from '@environments/environment';
import { NgIcon } from '@ng-icons/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DateInputCustomFormatPlaceholder } from '@progress/kendo-angular-dateinputs';
import {
    DialogActionsComponent,
    DialogContentBase,
    DialogRef,
    DialogTitleBarComponent
} from '@progress/kendo-angular-dialog';
import _ from 'lodash';
import { generateInputConfig, getInputName } from '../input/input.helpers';

export interface DialogFormOptions {
    title: string;
    titleIcons?: IconKey[];
    message?: string;
    model?: any;
    cancelText?: string;
    cancelIcon?: IconKey;
    cancelAction?: Function;
    confirmText?: string;
    confirmIcon?: IconKey;
    confirmMessage?: string;
    properties?: InputConfig[];
    showUserPresetControl?: boolean;
    listUrl?: string;
    queryName?: string;
    canEdit?: (model: any) => boolean;
    canConfirm?: (model: any) => boolean;
    contentComponent?: Type<any>;
    contentComponentDataProviders?: StaticProvider[];
    onContentComponentCreated?: (component: any) => void;
    selectedFilterPreset?: Signal<any | null>;
}

export function allRequiredFieldsFilled(model: any, properties: InputConfig[]): boolean {
    return properties.every((property) => {
        if (property.isRequired) {
            const value = model[property.id];
            return value !== null && value !== undefined && value !== '';
        }
        return true;
    });
}

@Component({
    selector: 'app-dialog-form',
    templateUrl: './dialog-form.component.html',
    imports: [
        DialogTitleBarComponent,
        NgIcon,
        TranslatePipe,
        DialogActionsComponent,
        NgStyle,
        ButtonComponent,
        InputComponent,
        FormsModule
    ]
})
export class DialogFormComponent extends DialogContentBase implements OnDestroy, AfterViewInit {
    private commonService = inject(CommonService);

    @ViewChild('template', { static: false, read: ViewContainerRef }) templateRef: ViewContainerRef;

    private contentComponentRef: ComponentRef<any>;
    private injector: Injector;

    protected generateInputConfig = generateInputConfig;
    protected getInputName = getInputName;

    public filterName = '';
    filterNameInput: InputConfig = {
        type: InputType.TEXT,
        label: 'Name your filter',
        title: 'filterName',
        isEditMode: true,
        value: this.filterName,
        id: 'filterName',
        isRequired: true
    };

    model: any;

    options: DialogFormOptions = {
        title: 'Form',
        cancelText: 'Cancel',
        confirmText: 'Confirm',
        showUserPresetControl: false
    };

    confirmAction: Function;
    canConfirmPredicate: (model: any) => boolean = () => true;
    canEditPredicate: (model: any) => boolean = () => true;
    cancelAction: Function;

    inputs: Signal<readonly BaseInputComponent<any>[]> = viewChildren(BaseInputComponent);
    private fileRestrictions = environment.settings.input.fileRestrictions;
    private popupSettings = environment.settings.input.dropdown.popupSettings;
    private dateFormatPlaceholder: DateInputCustomFormatPlaceholder = {
        day: this.commonService.translateService.instant('day'),
        month: this.commonService.translateService.instant('month'),
        year: this.commonService.translateService.instant('year'),
        hour: this.commonService.translateService.instant('hour'),
        minute: this.commonService.translateService.instant('minute'),
        second: this.commonService.translateService.instant('second'),
        millisecond: this.commonService.translateService.instant('millisecond')
    };

    @HostListener('document:keyup.escape', ['$event']) onEscapePress() {
        this.close();
    }

    @HostListener('document:keyup.enter', ['$event']) onEnterPress() {
        if (!this.canConfirmPredicate(this.model)) return;
        this.confirmAction();
    }

    constructor() {
        super(inject(DialogRef));

        effect(() => {
            if (!this.options.showUserPresetControl || !this.options.selectedFilterPreset) {
                return;
            }
            const name = this.options.selectedFilterPreset()?.name ?? '';
            this.filterName = name;

            // keep input config in sync
            this.filterNameInput = {
                ...this.filterNameInput,
                value: this.filterName
            };
        });
    }

    public initialize(options: DialogFormOptions, injector: Injector): void {
        for (const [key, value] of Object.entries(options)) {
            if (value != null) this.options[key] = value;
        }

        if (this.options.cancelAction) this.cancelAction = this.options.cancelAction;
        this.model = this.options.model || {};
        if (!this.model || Object.keys(this.model).length === 0)
            this.options.properties?.forEach((prop) => (this.model[prop.id] = prop.initialValue));
        this.injector = injector;
        this.canConfirmPredicate = this.options.canConfirm || this.canConfirm.bind(this);
        this.canEditPredicate = this.options.canEdit || this.canEdit.bind(this);
    }

    confirm(): void {
        this.inputs().forEach((input) => input.validate());
        if (this.inputs().some((x) => x.error())) {
            this.commonService.toastrNotificationService.show({
                type: 'error',
                message: 'Some required fields are empty'
            });
            return;
        }
        this.dialog.close(this.model);
    }

    canConfirm(_model: any): boolean {
        return true;
    }

    canEdit(_model: any): boolean {
        return true;
    }

    close(): void {
        this.dialog.close(null);
    }

    clear(): void {
        if (this.cancelAction) this.cancelAction();
        this.options.properties?.forEach((prop) => {
            prop.initialValue = null;
            this.model[this.getInputName(prop)] = null;
        });
    }

    private loadComponent(component, dataProviders: StaticProvider[]): void {
        dataProviders = (dataProviders || []).concat([{ provide: DialogRef, useValue: this.dialog }]);
        const injector = Injector.create({ providers: dataProviders || [], parent: this.injector });
        this.contentComponentRef = this.templateRef.createComponent(component, { injector });
        if (!this.contentComponentRef.instance.model) {
            this.contentComponentRef.instance.model = this.model;
        } else {
            this.model = this.contentComponentRef.instance.model;
        }

        if (_.isFunction(this.contentComponentRef.instance.confirm)) {
            this.confirmAction = () => {
                this.contentComponentRef.instance.confirm.bind(this.contentComponentRef.instance);
            };
        }

        if (_.isFunction(this.contentComponentRef.instance.canConfirm)) {
            this.canConfirmPredicate = this.contentComponentRef.instance.canConfirm.bind(
                this.contentComponentRef.instance
            );
        }

        if (_.isFunction(this.contentComponentRef.instance.canEdit)) {
            this.canEditPredicate = this.contentComponentRef.instance.canEdit.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.contentComponentRef.instance.cancel)) {
            this.cancelAction = this.contentComponentRef.instance.cancel.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.options.onContentComponentCreated)) {
            this.options.onContentComponentCreated(this.contentComponentRef.instance);
        }

        this.contentComponentRef.changeDetectorRef.detectChanges();
    }

    override ngAfterViewInit(): void {
        if (this.options.contentComponent) {
            this.loadComponent(this.options.contentComponent, this.options.contentComponentDataProviders);
        }

        this.confirmAction = this.confirmAction || this.confirm.bind(this);
        this.cancelAction = this.cancelAction || this.close.bind(this);
    }

    ngOnDestroy(): void {
        if (this.contentComponentRef) {
            this.contentComponentRef.destroy();
        }
    }

    protected async saveFilterPreset(): Promise<void> {
        const result = await this.commonService.dialogService.confirm({
            options: {
                title: this.commonService.translateService.instant('Save your filter'),
                message: `${this.commonService.translateService.instant('Are you sure you want to save your filter')}?`
            }
        });
        if (!result) return;

        await this.saveListFilter({
            id: this.options.selectedFilterPreset()?.id ?? 0,
            name: this.filterName,
            list: this.options.listUrl,
            query: this.options.queryName,
            value: JSON.stringify(this.model)
        });

        this.confirmAction();
    }

    private saveListFilter(model: ListFilterModel): Promise<void> {
        return this.commonService.executeCommand({
            command: 'SaveListFilter',
            data: {
                action: CrudAction.Create,
                saveModel: model
            },
            successToastrMessage: 'Filter preset saved!',
            showSpinner: true
        });
    }
}
